const WHATSAPP_LINK_SELECTOR = '[data-whatsapp-quote-link]';
const QUOTE_PANEL_SELECTOR = '[data-quote-invitation-panel]';
const STICKY_QUOTE_SELECTOR = '[data-whatsapp-sticky-bar]';
const STICKY_VISIBLE_CLASS = 'whatsapp-sticky-quote-visible';
const observedQuoteLinks = new WeakSet();

function getSelectedOptions(root) {
  const selectedValues = [];

  root.querySelectorAll('variant-selects input[type="radio"]:checked, input[name^="options["]:checked').forEach((input) => {
    if (input.value) selectedValues.push(input.value.trim());
  });

  root.querySelectorAll('variant-selects select, select[name^="options["]').forEach((select) => {
    const value = select.value?.trim();
    if (value) selectedValues.push(value);
  });

  return selectedValues.join(' / ');
}

function getSelectedQuantity(root) {
  return root.querySelector('.quantity__input')?.value?.trim() || '';
}

function getSelectedVariantId(root) {
  return root.querySelector('input[name="id"]')?.value?.trim() || root.querySelector('select[name="id"]')?.value?.trim() || '';
}

function getQuoteEventData(link) {
  const isDynamic = link.dataset.whatsappDynamic === 'true';
  const productRoot = isDynamic ? link.closest('product-info') : null;
  const root = productRoot || document;
  const rawPrice = Number(link.dataset.whatsappProductPrice || 0);

  return {
    event_version: '1',
    placement: link.dataset.whatsappPlacement || 'unknown',
    tier: link.dataset.whatsappTier || 'secondary',
    context: link.dataset.whatsappContext || '',
    cta_label: link.querySelector('.whatsapp-quote-link__label')?.textContent?.trim() || '',
    product_id: link.dataset.whatsappProductId || '',
    product_title: link.dataset.whatsappProductTitle || '',
    product_url: link.dataset.whatsappProductUrl || '',
    product_price: rawPrice ? rawPrice / 100 : 0,
    variant_id: isDynamic ? getSelectedVariantId(root) : link.dataset.whatsappVariantId || '',
    variant: isDynamic ? getSelectedOptions(root) : link.dataset.whatsappVariant || '',
    quantity: isDynamic ? getSelectedQuantity(root) : link.dataset.whatsappQuantity || '',
    page_type: link.dataset.whatsappPageType || '',
    page_url: window.location.href,
  };
}

function publishQuoteEvent(eventName, link) {
  const eventData = getQuoteEventData(link);

  if (window.Shopify?.analytics?.publish) {
    window.Shopify.analytics.publish(`va_cards:${eventName}`, eventData);
  }

  if (Array.isArray(window.dataLayer)) {
    window.dataLayer.push({
      event: eventName,
      ...eventData,
    });
  }

  if (typeof window.gtag === 'function') {
    window.gtag('event', eventName, eventData);
  }
}

function initQuoteImpressions(scope = document) {
  if (!('IntersectionObserver' in window)) return;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;

        publishQuoteEvent('whatsapp_quote_view', entry.target);
        observer.unobserve(entry.target);
      });
    },
    { threshold: 0.5 },
  );

  scope.querySelectorAll(WHATSAPP_LINK_SELECTOR).forEach((link) => {
    if (link.closest(STICKY_QUOTE_SELECTOR)) return;
    if (observedQuoteLinks.has(link)) return;
    observedQuoteLinks.add(link);
    observer.observe(link);
  });
}

function buildWhatsAppMessage(link) {
  const isDynamic = link.dataset.whatsappDynamic === 'true';
  const productRoot = isDynamic ? link.closest('product-info') : null;
  const productTitle = link.dataset.whatsappProductTitle || '';
  const productUrl = link.dataset.whatsappProductUrl || '';
  const context = link.dataset.whatsappContext || '';
  const variant = isDynamic ? getSelectedOptions(productRoot || document) : link.dataset.whatsappVariant || '';
  const quantity = isDynamic ? getSelectedQuantity(productRoot || document) : link.dataset.whatsappQuantity || '';
  const pageUrl = isDynamic ? window.location.href : link.dataset.whatsappPageUrl || window.location.href;
  const messageLines = ['Hola, quiero cotizar' + (productTitle ? ' este modelo.' : '.')];

  if (productTitle) messageLines.push('', `Producto: ${productTitle}`);
  if (variant) messageLines.push('', `Variante: ${variant}`);
  if (quantity) messageLines.push('', `Cantidad: ${quantity}`);
  if (context) messageLines.push('', `Origen: ${context}`);
  if (productUrl) messageLines.push('', `Enlace del producto: ${productUrl}`);
  if (pageUrl && pageUrl !== productUrl) messageLines.push('', `Página: ${pageUrl}`);

  return messageLines.join('\n');
}

function updateWhatsAppLink(link) {
  const phoneNumber = link.dataset.whatsappPhone || '50762168911';
  const text = encodeURIComponent(buildWhatsAppMessage(link));
  link.href = `https://wa.me/${phoneNumber}?text=${text}`;
}

function updateWhatsAppLinks(scope = document) {
  scope.querySelectorAll(WHATSAPP_LINK_SELECTOR).forEach(updateWhatsAppLink);
}

function setStickyQuoteVisible(bar, isVisible) {
  bar.classList.toggle('is-visible', isVisible);
  bar.setAttribute('aria-hidden', isVisible ? 'false' : 'true');
  bar.inert = !isVisible;
  document.body.classList.toggle(STICKY_VISIBLE_CLASS, isVisible);

  const quoteLink = bar.querySelector(WHATSAPP_LINK_SELECTOR);
  if (isVisible && quoteLink && !observedQuoteLinks.has(quoteLink)) {
    observedQuoteLinks.add(quoteLink);
    publishQuoteEvent('whatsapp_quote_view', quoteLink);
  }
}

function initStickyQuoteBars() {
  const quotePanels = document.querySelectorAll(QUOTE_PANEL_SELECTOR);
  const stickyBars = document.querySelectorAll(STICKY_QUOTE_SELECTOR);

  if (!quotePanels.length || !stickyBars.length) return;

  const primaryPanel = quotePanels[0];

  stickyBars.forEach((bar) => {
    const syncStickyState = () => {
      const panelRect = primaryPanel.getBoundingClientRect();
      setStickyQuoteVisible(bar, panelRect.bottom < 0);
    };

    syncStickyState();
    window.addEventListener('scroll', syncStickyState, { passive: true });
    window.addEventListener('resize', syncStickyState);

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(syncStickyState, { threshold: [0, 0.1, 1] });
      observer.observe(primaryPanel);
    }
  });
}

document.addEventListener('DOMContentLoaded', () => {
  updateWhatsAppLinks();
  initStickyQuoteBars();
  initQuoteImpressions();
});

document.addEventListener('product-info:loaded', (event) => {
  updateWhatsAppLinks(event.target);
  initQuoteImpressions(event.target);
});

document.addEventListener('change', (event) => {
  const productRoot = event.target.closest('product-info');
  if (productRoot) updateWhatsAppLinks(productRoot);
});

document.addEventListener('input', (event) => {
  const productRoot = event.target.closest('product-info');
  if (productRoot) updateWhatsAppLinks(productRoot);
});

document.addEventListener('click', (event) => {
  const link = event.target.closest(WHATSAPP_LINK_SELECTOR);
  if (!link) return;

  updateWhatsAppLink(link);
  publishQuoteEvent('whatsapp_quote_click', link);
});
